import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Modal,
  Badge,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import api from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import AccountsStoreFilterCombobox from './AccountsStoreFilterCombobox';

const ALIPAY_QR_IMAGE = `${process.env.PUBLIC_URL || ''}/images/QR.jpg`;

const quickChartQrDataUrl = (text) =>
  `https://quickchart.io/qr?text=${encodeURIComponent(text)}&size=280&margin=2`;

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// Динамический AliPay (page pay + опционально precreate QR). По умолчанию вкл.; скрыть: REACT_APP_BILLING_ALIPAY_UI=false
const BILLING_ALIPAY_DYNAMIC_UI = process.env.REACT_APP_BILLING_ALIPAY_UI !== 'false';
// QR precreate: только если и динамический UI включён выше, и REACT_APP_BILLING_ALIPAY_PRECREATE=true
const BILLING_ALIPAY_SHOW_PRECREATE = process.env.REACT_APP_BILLING_ALIPAY_PRECREATE === 'true';

/** Динамический блок «Pay with AliPay» (page pay / опционально precreate QR) — только для этого аккаунта. */
const BILLING_ALIPAY_DYNAMIC_UI_ALLOWED_EMAIL = 'career@indigolab.io';
/** Экспорт PDF отчёта Accounts — пока только для внутреннего аккаунта (Excel для всех). */
const BILLING_ACCOUNTS_PDF_EXPORT_ALLOWED_EMAIL = 'career@indigolab.io';

function formatYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function mondayWeekStart(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const wd = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - wd);
  return x;
}

function initialWeekDateRange() {
  const today = new Date();
  const cal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return { from: formatYmd(mondayWeekStart(cal)), to: formatYmd(cal) };
}

const STORAGE_MERGE_TYPES = new Set(['storage_daily', 'storage_warehouse']);

/** Порядок строк в сводке «за что платите» (остальные типы — в конце по алфавиту). */
const ACCOUNTS_SUMMARY_TYPE_ORDER = [
  'receipt_warehouse',
  'receipt_pickup',
  'inspection_no_media',
  'inspection_with_media',
  'packaging_shipment',
  'storage_at_warehouse',
  'prepare_return_to_seller',
  'disposal',
  'balance_top_up',
];

function sortAccountsChargeTypeEntries(entries) {
  return [...entries].sort((a, b) => {
    const ia = ACCOUNTS_SUMMARY_TYPE_ORDER.indexOf(a[0]);
    const ib = ACCOUNTS_SUMMARY_TYPE_ORDER.indexOf(b[0]);
    if (ia === -1 && ib === -1) return a[0].localeCompare(b[0]);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

function logicalChargeType(type) {
  if (!type) return type;
  if (STORAGE_MERGE_TYPES.has(type)) return 'storage_at_warehouse';
  return type;
}

const CHARGE_NAME_KEYS = {
  receipt_warehouse: 'billing.accountsChargeNameReceiptWarehouse',
  receipt_pickup: 'billing.accountsChargeNameReceiptPickup',
  inspection_no_media: 'billing.accountsChargeNameInspectionNoMedia',
  inspection_with_media: 'billing.accountsChargeNameInspectionWithMedia',
  packaging_shipment: 'billing.accountsChargeNamePackagingShipment',
  storage_at_warehouse: 'billing.accountsChargeNameStorageAtWarehouse',
  prepare_return_to_seller: 'billing.accountsChargeNamePrepareReturn',
  disposal: 'billing.accountsChargeNameDisposal',
  balance_top_up: 'billing.accountsChargeNameBalanceTopUp',
  custom: 'billing.accountsCustomChargesTitle',
};

const CHARGE_TOOLTIP_KEYS = {
  receipt_warehouse: 'billing.accountsChargeTooltipReceiptWarehouse',
  receipt_pickup: 'billing.accountsChargeTooltipReceiptPickup',
  inspection_no_media: 'billing.accountsChargeTooltipInspectionNoMedia',
  inspection_with_media: 'billing.accountsChargeTooltipInspectionWithMedia',
  packaging_shipment: 'billing.accountsChargeTooltipPackagingShipment',
  storage_at_warehouse: 'billing.accountsChargeTooltipStorageAtWarehouse',
  prepare_return_to_seller: 'billing.accountsChargeTooltipPrepareReturn',
  disposal: 'billing.accountsChargeTooltipDisposal',
  balance_top_up: 'billing.accountsChargeTooltipBalanceTopUp',
};

function translateOrderStatus(t, code, displayFallback) {
  const map = {
    awaiting_receipt: 'billing.accountsStatusAwaitingReceipt',
    received_awaiting_check: 'billing.accountsStatusReceivedAwaitingCheck',
    awaiting_seller_decision: 'billing.accountsStatusAwaitingSellerDecision',
    awaiting_for_sale: 'billing.accountsStatusAwaitingForSale',
    transferring_to_sale: 'billing.accountsStatusTransferringToSale',
    submitted_for_sale: 'billing.accountsStatusSubmittedForSale',
    awaiting_shipment: 'billing.accountsStatusAwaitingShipment',
    awaiting_disposal: 'billing.accountsStatusAwaitingDisposal',
    disposed: 'billing.accountsStatusDisposed',
    sent_to_seller: 'billing.accountsStatusSentToSeller',
  };
  const k = code && map[code];
  if (!k) return displayFallback || code || '—';
  const v = t(k);
  return v === k ? (displayFallback || code || '—') : v;
}

function chargeTypeLabel(t, logicalKey) {
  const nk = CHARGE_NAME_KEYS[logicalKey];
  if (!nk) return logicalKey;
  const v = t(nk);
  return v === nk ? logicalKey : v;
}

function aggregateLogicalExpenses(expenses) {
  const map = new Map();
  (expenses || []).forEach((ex) => {
    const lk = logicalChargeType(ex.type);
    if (!map.has(lk)) map.set(lk, { total: 0, count: 0 });
    const row = map.get(lk);
    row.total += parseFloat(ex.amount, 10) || 0;
    row.count += 1;
  });
  return Array.from(map.entries())
    .map(([key, v]) => ({ key, ...v }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

function ChargeHint({ t, logicalType }) {
  const tipKey = CHARGE_TOOLTIP_KEYS[logicalType];
  if (!tipKey) return null;
  const text = t(tipKey);
  if (!text || text === tipKey) return null;
  return (
    <OverlayTrigger
      placement="auto"
      overlay={<Tooltip id={`ch-${logicalType}`} className="billing-charge-tooltip">{text}</Tooltip>}
    >
      <span className="ms-1 text-muted billing-charge-hint" role="button" tabIndex={0} style={{ cursor: 'help' }}>
        ?
      </span>
    </OverlayTrigger>
  );
}

function sumExpensesAmount(expenses) {
  return (expenses || []).reduce((s, ex) => s + (parseFloat(ex.amount, 10) || 0), 0);
}

function accountsChargeLinesKey(accountRowKeyStr, orderId) {
  return `${accountRowKeyStr}-${orderId}`;
}

function accountRowKey(row) {
  const sid = row.seller_id != null ? String(row.seller_id) : '';
  const bc = row.barcode != null ? String(row.barcode) : '';
  return `${sid}\x1f${bc}`;
}

function accountsThumbPairKey(sellerId, nmId) {
  return `${String(sellerId)}\x1f${String(Number(nmId))}`;
}

const BillingsPage = ({ onOpen, onNavigateToSupport }) => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const userEmailLower = (user?.email || '').toLowerCase();
  const showAlipayDynamicUi =
    BILLING_ALIPAY_DYNAMIC_UI &&
    userEmailLower === BILLING_ALIPAY_DYNAMIC_UI_ALLOWED_EMAIL.toLowerCase();
  const showAccountsPdfExport =
    userEmailLower === BILLING_ACCOUNTS_PDF_EXPORT_ALLOWED_EMAIL.toLowerCase();
  const showTopUpTransactions = !user?.is_superuser;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [balanceTopUp, setBalanceTopUp] = useState('');
  const [totalDeductions, setTotalDeductions] = useState('');
  const [currentBalance, setCurrentBalance] = useState('');
  const [storesBalance, setStoresBalance] = useState([]);

  const [weekStats, setWeekStats] = useState(null);
  const [weekLoading, setWeekLoading] = useState(false);

  const [dateFrom, setDateFrom] = useState(() => initialWeekDateRange().from);
  const [dateTo, setDateTo] = useState(() => initialWeekDateRange().to);
  const [quickPeriod, setQuickPeriod] = useState('week');
  const [search, setSearch] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [exporting, setExporting] = useState(null); // null | 'excel' | 'pdf'

  const [barcodeRows, setBarcodeRows] = useState([]);
  const [barcodeTotal, setBarcodeTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [topUpRows, setTopUpRows] = useState([]);
  const [topUpTotal, setTopUpTotal] = useState(0);
  const [topUpsLoading, setTopUpsLoading] = useState(false);
  const [topUpsExporting, setTopUpsExporting] = useState(false);
  const [customChargeRows, setCustomChargeRows] = useState([]);
  const [customChargeTotal, setCustomChargeTotal] = useState(0);
  const [customChargesLoading, setCustomChargesLoading] = useState(false);
  const [customChargesExporting, setCustomChargesExporting] = useState(false);

  const [expandedAccountKey, setExpandedAccountKey] = useState(null);
  const [expandedChargeLines, setExpandedChargeLines] = useState({});
  const [detailByAccountKey, setDetailByAccountKey] = useState({});
  const [detailLoading, setDetailLoading] = useState(null);
  const [accountsTableSort, setAccountsTableSort] = useState('store');
  const [accountsThumbsLoading, setAccountsThumbsLoading] = useState(false);
  const [isMobileAccountsView, setIsMobileAccountsView] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 992 : false,
  );

  const [showQrModal, setShowQrModal] = useState(false);
  const [alipayPayModal, setAlipayPayModal] = useState(null);
  const [alipayPayLoadingPk, setAlipayPayLoadingPk] = useState(null);
  const [alipayPayError, setAlipayPayError] = useState('');
  const [alipayPayloadCopied, setAlipayPayloadCopied] = useState(false);

  const loadBalance = useCallback(async () => {
    try {
      const response = await api.getBillingExpenses({ page: 1, page_size: 1 });
      if (response?.balance_top_up != null) setBalanceTopUp(response.balance_top_up);
      if (response?.total_deductions != null) setTotalDeductions(response.total_deductions);
      if (response?.current_balance != null) setCurrentBalance(response.current_balance);
      setStoresBalance(Array.isArray(response?.stores_balance) ? response.stores_balance : []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const applyQuickPeriod = useCallback((key) => {
    if (key === 'all') {
      setDateFrom('');
      setDateTo('');
      return;
    }
    const today = new Date();
    const cal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    let fromD;
    if (key === 'today') {
      fromD = cal;
    } else if (key === 'week') {
      fromD = mondayWeekStart(cal);
    } else if (key === 'month') {
      fromD = new Date(cal.getFullYear(), cal.getMonth(), 1);
    } else if (key === 'quarter') {
      const q0 = Math.floor(cal.getMonth() / 3) * 3;
      fromD = new Date(cal.getFullYear(), q0, 1);
    } else {
      return;
    }
    setDateFrom(formatYmd(fromD));
    setDateTo(formatYmd(cal));
  }, []);

  const loadWeek = useCallback(async () => {
    setWeekLoading(true);
    try {
      const params = {};
      if (storeFilter) params.store_id = storeFilter;
      if (quickPeriod === 'all') {
        params.period = 'all';
      } else {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      if (search.trim()) params.search = search.trim();
      const res = await api.getBillingAccountsWeekSummary(params);
      setWeekStats(res?.data || null);
    } catch (err) {
      console.error(err);
      setWeekStats(null);
    } finally {
      setWeekLoading(false);
    }
  }, [storeFilter, dateFrom, dateTo, search, quickPeriod]);

  const loadTopUps = useCallback(async () => {
    if (!showTopUpTransactions) return;
    setTopUpsLoading(true);
    try {
      const params = { limit: 100 };
      if (storeFilter) params.store_id = storeFilter;
      if (quickPeriod === 'all') {
        params.period = 'all';
      } else {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      const res = await api.getBillingAccountsTopUps(params);
      setTopUpRows(Array.isArray(res?.data) ? res.data : []);
      setTopUpTotal(res?.count ?? 0);
    } catch (err) {
      console.error(err);
      setTopUpRows([]);
      setTopUpTotal(0);
    } finally {
      setTopUpsLoading(false);
    }
  }, [showTopUpTransactions, storeFilter, dateFrom, dateTo, quickPeriod]);

  const loadCustomCharges = useCallback(async () => {
    setCustomChargesLoading(true);
    try {
      const params = { limit: 100 };
      if (storeFilter) params.store_id = storeFilter;
      if (quickPeriod === 'all') {
        params.period = 'all';
      } else {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      const res = await api.getBillingAccountsCustomCharges(params);
      setCustomChargeRows(Array.isArray(res?.data) ? res.data : []);
      setCustomChargeTotal(res?.count ?? 0);
    } catch (err) {
      console.error(err);
      setCustomChargeRows([]);
      setCustomChargeTotal(0);
    } finally {
      setCustomChargesLoading(false);
    }
  }, [storeFilter, dateFrom, dateTo, quickPeriod]);

  const loadBarcodePage = useCallback(async () => {
    setLoading(true);
    setAccountsThumbsLoading(false);
    setError('');
    try {
      const params = {
        page,
        page_size: pageSize,
        include_wb_media: '0',
      };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search.trim()) params.search = search.trim();
      if (storeFilter) params.store_id = storeFilter;
      const response = await api.getBillingAccountsByBarcode(params);
      const rows = Array.isArray(response?.data) ? response.data : [];
      setBarcodeRows(rows);
      setBarcodeTotal(response?.count ?? 0);
      setLoading(false);

      const thumbItems = rows
        .filter(
          (r) =>
            r.nm_id != null &&
            r.seller_id &&
            r.seller_id !== '__unknown__' &&
            !r.wb_image_url,
        )
        .map((r) => ({ seller_id: String(r.seller_id), nm_id: Number(r.nm_id) }));
      if (thumbItems.length > 0) {
        setAccountsThumbsLoading(true);
        try {
          const thumbs = await api.postBillingAccountsBarcodeThumbnails({ items: thumbItems });
          const urlByPair = new Map();
          (thumbs.images || []).forEach((x) => {
            if (x && x.seller_id != null && x.nm_id != null) {
              urlByPair.set(accountsThumbPairKey(x.seller_id, x.nm_id), x.wb_image_url);
            }
          });
          setBarcodeRows((prev) =>
            prev.map((r) => {
              if (r.nm_id == null || !r.seller_id || r.seller_id === '__unknown__') return r;
              const u = urlByPair.get(accountsThumbPairKey(r.seller_id, r.nm_id));
              if (u === undefined) return r;
              return { ...r, wb_image_url: u };
            }),
          );
        } catch (thumbErr) {
          console.error(thumbErr);
        } finally {
          setAccountsThumbsLoading(false);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load accounts');
      setBarcodeRows([]);
      setBarcodeTotal(0);
      setLoading(false);
      setAccountsThumbsLoading(false);
    }
  }, [dateFrom, dateTo, search, storeFilter, page, pageSize]);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  useEffect(() => {
    if (typeof onOpen === 'function') onOpen();
  }, [onOpen]);

  useEffect(() => {
    loadWeek();
  }, [loadWeek]);

  useEffect(() => {
    loadTopUps();
  }, [loadTopUps]);

  useEffect(() => {
    loadCustomCharges();
  }, [loadCustomCharges]);

  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, search, storeFilter, pageSize]);

  useEffect(() => {
    setExpandedAccountKey(null);
    setExpandedChargeLines({});
    setDetailByAccountKey({});
  }, [dateFrom, dateTo, quickPeriod, storeFilter]);

  useEffect(() => {
    loadBarcodePage();
  }, [loadBarcodePage]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileAccountsView(window.innerWidth < 992);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get('view') !== 'billings') return;
      const s = p.get('accounts_search');
      const nm = p.get('accounts_nm');
      const st = p.get('accounts_store');
      if (s) setSearch(s);
      else if (nm) setSearch(nm);
      if (st) setStoreFilter(st);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const sortedBarcodeRows = useMemo(() => {
    const rows = Array.isArray(barcodeRows) ? [...barcodeRows] : [];
    const byStore = (a, b) => {
      const na = (a.store_name || a.seller_id || '').toString().toLowerCase();
      const nb = (b.store_name || b.seller_id || '').toString().toLowerCase();
      if (na !== nb) return na.localeCompare(nb);
      return accountRowKey(a).localeCompare(accountRowKey(b));
    };
    const byProduct = (a, b) => {
      const na = (a.product_name || '').toLowerCase();
      const nb = (b.product_name || '').toLowerCase();
      if (na !== nb) return na.localeCompare(nb);
      return byStore(a, b);
    };
    const byBarcode = (a, b) => {
      const ba = (a.barcode || '').toString();
      const bb = (b.barcode || '').toString();
      if (ba !== bb) return ba.localeCompare(bb);
      return byStore(a, b);
    };
    const byCharges = (a, b) => {
      const da = parseFloat(String(a.total_charges_cny).replace(',', '.')) || 0;
      const db = parseFloat(String(b.total_charges_cny).replace(',', '.')) || 0;
      if (da !== db) return da - db;
      return byStore(a, b);
    };
    if (accountsTableSort === 'product') rows.sort(byProduct);
    else if (accountsTableSort === 'barcode') rows.sort(byBarcode);
    else if (accountsTableSort === 'charges') rows.sort(byCharges);
    else rows.sort(byStore);
    return rows;
  }, [barcodeRows, accountsTableSort]);

  const toggleExpand = async (row) => {
    const key = accountRowKey(row);
    const bc = row.barcode;
    if (expandedAccountKey === key) {
      setExpandedAccountKey(null);
      return;
    }
    setExpandedChargeLines({});
    setExpandedAccountKey(key);
    if (detailByAccountKey[key]) return;
    setDetailLoading(key);
    try {
      const params = { barcode: bc };
      if (row.seller_id && row.seller_id !== '__unknown__') {
        params.seller_id = row.seller_id;
      }
      if (storeFilter) params.store_id = storeFilter;
      if (quickPeriod === 'all') {
        params.period = 'all';
      } else {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      const data = await api.getBillingAccountsBarcodeDetail(params);
      setDetailByAccountKey((prev) => ({ ...prev, [key]: data }));
    } catch (err) {
      setDetailByAccountKey((prev) => ({
        ...prev,
        [key]: { error: err.message || 'Failed to load details' },
      }));
    } finally {
      setDetailLoading(null);
    }
  };

  const handleExport = async () => {
    setExporting('excel');
    try {
      const params = { lang: language || 'en' };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search.trim()) params.search = search.trim();
      if (storeFilter) params.store_id = storeFilter;
      const res = await api.downloadBillingAccountsBarcodeExcel(params);
      const blob = res?.data;
      if (!blob) throw new Error('No file received');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('billing.accountsExportFilename')}_${excelFilenameSuffix()}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  const handleCustomChargesExport = async () => {
    setCustomChargesExporting(true);
    try {
      const params = { lang: language || 'en' };
      if (storeFilter) params.store_id = storeFilter;
      if (quickPeriod === 'all') {
        params.period = 'all';
      } else {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      const res = await api.downloadBillingAccountsCustomChargesExcel(params);
      const blob = res?.data;
      if (!blob) throw new Error('No file received');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('billing.accountsCustomChargesExportFilename')}_${excelFilenameSuffix()}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setCustomChargesExporting(false);
    }
  };

  const handleTopUpsExport = async () => {
    setTopUpsExporting(true);
    try {
      const params = { lang: language || 'en' };
      if (storeFilter) params.store_id = storeFilter;
      if (quickPeriod === 'all') {
        params.period = 'all';
      } else {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      const res = await api.downloadBillingAccountsTopUpsExcel(params);
      const blob = res?.data;
      if (!blob) throw new Error('No file received');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('billing.accountsTopUpsExportFilename')}_${excelFilenameSuffix()}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setTopUpsExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!showAccountsPdfExport) return;
    setExporting('pdf');
    try {
      const params = { lang: language || 'en' };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search.trim()) params.search = search.trim();
      if (storeFilter) params.store_id = storeFilter;
      const res = await api.downloadBillingAccountsBarcodePdf(params);
      const blob = res?.data;
      if (!blob) throw new Error('No file received');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('billing.accountsPdfFilename')}_${pdfReportFilenameSuffix()}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      setError(err.message || 'PDF export failed');
    } finally {
      setExporting(null);
    }
  };

  const storesNeedingAlipayPay = storesBalance.filter((s) => {
    const n = parseFloat(String(s.total_amount).replace(',', '.'));
    return !Number.isNaN(n) && n < 0;
  });

  const handleAlipayPrecreate = async (store) => {
    setAlipayPayError('');
    setAlipayPayLoadingPk(store.store_id);
    try {
      const data = await api.createBillingAlipayQr(store.store_id);
      setAlipayPayModal({
        storeName: store.store_name,
        ...data,
      });
    } catch (e) {
      const d = e?.data;
      const sub = d?.alipay?.sub_msg || d?.alipay?.detail;
      setAlipayPayError(sub || e?.message || 'Request failed');
    } finally {
      setAlipayPayLoadingPk(null);
    }
  };

  const handleAlipayPagePay = async (store, { newTab = false } = {}) => {
    setAlipayPayError('');
    setAlipayPayLoadingPk(store.store_id);
    try {
      const data = await api.createBillingAlipayPagePay(store.store_id);
      if (data?.pay_url) {
        if (newTab) {
          const opened = window.open(data.pay_url, '_blank', 'noopener,noreferrer');
          if (!opened) {
            setAlipayPayError(t('billing.payWithAlipayPagePopupBlocked'));
          }
        } else {
          window.location.assign(data.pay_url);
        }
      } else {
        setAlipayPayError(t('billing.payWithAlipayPageNoUrl'));
      }
    } catch (e) {
      const d = e?.data;
      const sub = d?.alipay?.sub_msg || d?.alipay?.detail;
      setAlipayPayError(sub || e?.message || 'Request failed');
    } finally {
      setAlipayPayLoadingPk(null);
    }
  };

  const copyAlipayPayload = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setAlipayPayloadCopied(true);
      setTimeout(() => setAlipayPayloadCopied(false), 2000);
    } catch (_) {
      setAlipayPayError('Copy failed');
    }
  };

  const totalPages = Math.max(1, Math.ceil(barcodeTotal / pageSize));
  const from = barcodeTotal === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, barcodeTotal);

  const weekByTypeEntries = weekStats?.charges_week_by_type
    ? Object.entries(weekStats.charges_week_by_type)
    : [];
  const sortedWeekByTypeEntries = sortAccountsChargeTypeEntries(weekByTypeEntries);

  const excelFilenameSuffix = () => {
    const from = dateFrom || 'any';
    const to = dateTo || 'any';
    return `${from}_${to}_${language || 'en'}.xlsx`;
  };

  const pdfReportFilenameSuffix = () => {
    const from = dateFrom || 'any';
    const to = dateTo || 'any';
    return `${from}_${to}_${language || 'en'}.pdf`;
  };

  return (
    <div className="billings-page-shell billing-page-full w-100">
    <Container fluid className="px-0 billing-page-full-inner">
      <Card className="mb-0 border-0 shadow-sm billing-accounts-main-card h-100 d-flex flex-column">
        <Card.Header as="h5" className="bg-transparent border-bottom py-3">
          {t('billing.title')}
        </Card.Header>
        <Card.Body className="p-2 p-sm-3 p-md-4 flex-grow-1 d-flex flex-column" style={{ overflow: 'visible', minHeight: 0 }}>
          <Alert variant="warning" className="mb-2 py-3 small border-2 billing-deposit-banner" style={{ borderColor: 'var(--bs-warning)', backgroundColor: 'rgba(255,193,7,0.15)' }}>
            {t('billing.depositWarningBanner')}
          </Alert>
          {currentBalance !== '' && (
            <Row className="mb-3">
              <Col>
                <Card className="billing-balance-surface border-0">
                  <Card.Body className="py-2 px-3">
                    <strong>{t('billing.currentBalance')}:</strong>{' '}
                    <span className={parseFloat(currentBalance) >= 0 ? 'text-success' : 'text-danger'}>
                      {currentBalance} {t('common.currency.yen')}
                    </span>
                    {' · '}
                    <small className="text-muted">
                      {t('billing.topUp')}: {balanceTopUp} {t('common.currency.yen')} · {t('billing.deducted')}: {totalDeductions} {t('common.currency.yen')}
                    </small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
          {storesBalance.length > 0 && (
            <Row className="mb-3">
              <Col>
                <h6 className="text-muted mb-2">{t('billing.byStore')}</h6>
                <div className="d-flex flex-wrap gap-2">
                  {storesBalance.map((s) => (
                    <Card key={s.store_id} className="billing-balance-surface border-0 flex-grow-1" style={{ minWidth: '180px' }}>
                      <Card.Body className="py-2 px-3 small">
                        <div className="fw-bold text-truncate" title={s.store_name}>{s.store_name}</div>
                        <div className="text-muted">
                          {t('billing.deducted')}: <span className="billing-store-amount">{s.total_deductions} {t('common.currency.yen')}</span>
                        </div>
                        <div className={parseFloat(s.total_amount) >= 0 ? 'text-success' : 'text-danger'}>
                          {s.total_amount} {t('common.currency.yen')}
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </Col>
            </Row>
          )}

          <Card className="mb-4 border-primary border-2 bg-body-secondary billing-accounts-summary-card">
            <Card.Header className="py-3">
              <div className="fw-semibold fs-5 mb-3 billing-accounts-summary-heading">
                {t('billing.accountsPeriodSummaryTitle')}
              </div>
              <div className="billing-summary-dates-prominent rounded px-3 py-2 border border-primary border-opacity-50">
                <div className="small text-uppercase billing-summary-dates-label mb-1">
                  {t('billing.accountsSelectedPeriodLabel')}
                </div>
                <div className="fs-5 fw-bold billing-summary-dates-value">
                  {quickPeriod === 'all' || (!dateFrom && !dateTo)
                    ? t('billing.accountsAllTimeLabel')
                    : t('billing.accountsPeriodRange', {
                        from: dateFrom || '—',
                        to: dateTo || '—',
                      })}
                </div>
              </div>
            </Card.Header>
            <Card.Body className="py-3 pt-0">
              {weekLoading ? (
                <div className="py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : weekStats ? (
                <>
                  <div className="d-flex flex-wrap align-items-baseline justify-content-between gap-2 gap-md-3 mb-3 pb-3 border-bottom border-opacity-50">
                    <span className="text-muted">{t('billing.accountsSummaryTotalTitle')}</span>
                    <span className="fs-2 fw-bold text-danger billing-accounts-summary-total">
                      {weekStats.charges_week_total_cny} {t('common.currency.yen')}
                    </span>
                  </div>
                  <h6 className="text-muted small text-uppercase letter-spacing mb-2">
                    {t('billing.accountsSummaryByService')}
                  </h6>
                  {sortedWeekByTypeEntries.length === 0 ? (
                    <p className="small text-muted mb-4">{t('billing.accountsSummaryNoCharges')}</p>
                  ) : (
                    <Table borderless size="sm" className="mb-4 billing-accounts-summary-breakdown">
                      <tbody>
                        {sortedWeekByTypeEntries.map(([typeCode, amt]) => (
                          <tr key={typeCode}>
                            <td className="text-body py-2 align-middle">
                              {chargeTypeLabel(t, typeCode)}
                              <ChargeHint t={t} logicalType={typeCode} />
                            </td>
                            <td className="text-end text-danger py-2 align-middle text-nowrap fw-semibold">
                              {amt} {t('common.currency.yen')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                  <h6 className="text-muted small text-uppercase letter-spacing mb-2">
                    {t('billing.accountsSummaryActivityTitle')}
                  </h6>
                  <p className="small text-body mb-2">
                    {t('billing.accountsSummaryNewReturnsLine', {
                      total: weekStats.new_orders_week,
                      our: weekStats.created_orders_our_pvz_week,
                      other: weekStats.created_orders_other_pvz_week,
                    })}
                  </p>
                  <p className="small text-body mb-3">
                    {t('billing.accountsSummaryCompletedLine', {
                      total: weekStats.processed_to_final_week,
                      our: weekStats.processed_to_final_our_pvz_week,
                      other: weekStats.processed_to_final_other_pvz_week,
                    })}
                  </p>
                  <p className="small text-muted mb-0">{t('billing.accountsSummaryTableHint')}</p>
                </>
              ) : (
                <span className="text-muted">—</span>
              )}
            </Card.Body>
          </Card>

          <Row className="mb-4 g-3 align-items-stretch">
            <Col
              xs={12}
              md={showAlipayDynamicUi ? 6 : 12}
              lg={showAlipayDynamicUi ? 5 : 12}
              className="d-flex justify-content-center justify-content-md-start"
            >
              <Card className="border-0 shadow-sm billing-balance-surface billing-qr-card w-100" style={{ maxWidth: 320 }}>
                <Card.Body className="text-center p-4">
                  <img
                    src={ALIPAY_QR_IMAGE}
                    alt="AliPay QR"
                    className="img-fluid mb-3"
                    style={{ maxHeight: 220, objectFit: 'contain', cursor: 'pointer' }}
                    onClick={() => setShowQrModal(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowQrModal(true); }}
                  />
                  <Modal show={showQrModal} onHide={() => setShowQrModal(false)} centered size="lg">
                    <Modal.Header closeButton />
                    <Modal.Body className="text-center p-4 billing-modal-surface">
                      <img src={ALIPAY_QR_IMAGE} alt="AliPay QR" className="img-fluid" style={{ maxHeight: '80vh', width: 'auto' }} />
                    </Modal.Body>
                  </Modal>
                  <p className="small text-muted mb-3">{t('billing.sendReceiptHint')}</p>
                  {typeof onNavigateToSupport === 'function' && (
                    <Button variant="primary" onClick={() => onNavigateToSupport({ subject: 'Send receipt' })}>
                      {t('billing.sendReceiptButton')}
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </Col>
            {showAlipayDynamicUi && (
              <>
                <Col xs={12} md={6} lg={7}>
                  <Card className="border-0 shadow-sm billing-balance-surface h-100">
                    <Card.Body className="p-4">
                      <h6 className="mb-2">{t('billing.payWithAlipay')}</h6>
                      <p className="small text-muted mb-2">{t('billing.payWithAlipayPagePrimaryHint')}</p>
                      <p className="small text-muted mb-3">{t('billing.payWithAlipayHint')}</p>
                      {alipayPayError && (
                        <Alert variant="danger" className="py-2 small mb-3" dismissible onClose={() => setAlipayPayError('')}>
                          {alipayPayError}
                        </Alert>
                      )}
                      {storesNeedingAlipayPay.length === 0 ? (
                        <p className="small text-muted mb-0">{t('billing.payWithAlipayNegativeOnly')}</p>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {storesNeedingAlipayPay.map((s) => (
                            <div
                              key={s.store_id}
                              className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-2 rounded bg-white border"
                            >
                              <div className="small">
                                <div className="fw-semibold text-truncate" style={{ maxWidth: 260 }} title={s.store_name}>
                                  {s.store_name}
                                </div>
                                <div className="text-danger">
                                  {s.total_amount} {t('common.currency.yen')}
                                </div>
                              </div>
                              <div className="d-flex flex-wrap gap-2 align-items-center">
                                <Button
                                  variant="primary"
                                  size="sm"
                                  disabled={alipayPayLoadingPk != null}
                                  onClick={() => handleAlipayPagePay(s, { newTab: false })}
                                >
                                  {alipayPayLoadingPk === s.store_id ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    t('billing.payWithAlipayPageButton')
                                  )}
                                </Button>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-secondary py-0"
                                  disabled={alipayPayLoadingPk != null}
                                  onClick={() => handleAlipayPagePay(s, { newTab: true })}
                                >
                                  {t('billing.payWithAlipayPageNewTab')}
                                </Button>
                                {BILLING_ALIPAY_SHOW_PRECREATE && (
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={alipayPayLoadingPk != null}
                                    onClick={() => handleAlipayPrecreate(s)}
                                  >
                                    {alipayPayLoadingPk === s.store_id ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      t('billing.payWithAlipayButton')
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Modal
                  show={!!alipayPayModal}
                  onHide={() => { setAlipayPayModal(null); setAlipayPayloadCopied(false); }}
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title className="h6">
                      {t('billing.payWithAlipay')}
                      {alipayPayModal?.storeName && (
                        <span className="text-muted fw-normal"> — {alipayPayModal.storeName}</span>
                      )}
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body className="text-center">
                    {alipayPayModal?.reused && (
                      <Badge bg="secondary" className="mb-2">{t('billing.payWithAlipayReused')}</Badge>
                    )}
                    {alipayPayModal?.qr_code && (
                      <img
                        src={quickChartQrDataUrl(alipayPayModal.qr_code)}
                        alt=""
                        className="img-fluid mb-3"
                        style={{ maxWidth: 280 }}
                      />
                    )}
                    <p className="small text-muted mb-2">{t('billing.payWithAlipayScanHint')}</p>
                    {alipayPayModal?.amount != null && (
                      <p className="small mb-1">
                        <strong>{t('billing.payWithAlipayAmount')}:</strong>{' '}
                        {alipayPayModal.amount} {t('common.currency.yen')}
                      </p>
                    )}
                    {alipayPayModal?.out_trade_no && (
                      <p className="small mb-3 text-break">
                        <strong>{t('billing.payWithAlipayOrderNo')}:</strong> {alipayPayModal.out_trade_no}
                      </p>
                    )}
                    {alipayPayModal?.qr_code && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="mb-3"
                        onClick={() => copyAlipayPayload(alipayPayModal.qr_code)}
                      >
                        {alipayPayloadCopied ? t('billing.payWithAlipayCopied') : t('billing.payWithAlipayCopyPayload')}
                      </Button>
                    )}
                    <p className="small text-muted mb-0">{t('billing.payWithAlipayCreditHint')}</p>
                  </Modal.Body>
                </Modal>
              </>
            )}
          </Row>

          {error && <Alert variant="danger">{error}</Alert>}

          <h6 className="text-muted mb-2">{t('billing.product')}</h6>
          <Row className="mb-3 g-2 align-items-end">
            {storesBalance.length >= 1 && (
              <Col xs={12} md={3}>
                <AccountsStoreFilterCombobox
                  stores={storesBalance}
                  value={storeFilter}
                  onChange={setStoreFilter}
                  label={t('billing.byStore')}
                  allStoresLabel={t('billing.accountsAllStores')}
                  placeholder={t('billing.accountsStoreFilterPlaceholder')}
                  noMatchesText={t('billing.accountsStoreNoMatches')}
                />
              </Col>
            )}
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label className="small">{t('billing.accountsSortLabel')}</Form.Label>
                <Form.Select
                  value={accountsTableSort}
                  onChange={(e) => setAccountsTableSort(e.target.value)}
                >
                  <option value="store">{t('billing.accountsSortStore')}</option>
                  <option value="product">{t('billing.accountsSortProduct')}</option>
                  <option value="barcode">{t('billing.accountsSortBarcode')}</option>
                  <option value="charges">{t('billing.accountsSortCharges')}</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={2}>
              <Form.Group>
                <Form.Label className="small">{t('billing.accountsQuickPeriod')}</Form.Label>
                <Form.Select
                  value={quickPeriod}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === 'custom') {
                      setQuickPeriod('custom');
                      return;
                    }
                    setQuickPeriod(v);
                    applyQuickPeriod(v);
                  }}
                >
                  <option value="custom">{t('billing.accountsQuickPeriodCustom')}</option>
                  <option value="all">{t('billing.accountsQuickAllTime')}</option>
                  <option value="today">{t('billing.accountsQuickToday')}</option>
                  <option value="week">{t('billing.accountsQuickWeek')}</option>
                  <option value="month">{t('billing.accountsQuickMonth')}</option>
                  <option value="quarter">{t('billing.accountsQuickQuarter')}</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6} md={2}>
              <Form.Group>
                <Form.Label className="small">{t('billing.dateFrom')}</Form.Label>
                <Form.Control
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setQuickPeriod('custom');
                  }}
                />
              </Form.Group>
            </Col>
            <Col xs={6} md={2}>
              <Form.Group>
                <Form.Label className="small">{t('billing.dateTo')}</Form.Label>
                <Form.Control
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setQuickPeriod('custom');
                  }}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={2}>
              <Form.Group>
                <Form.Label className="small">{t('billing.searchProductBarcode')}</Form.Label>
                <Form.Control type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
              </Form.Group>
            </Col>
            <Col xs={12} md={2} className="d-flex flex-column align-items-stretch justify-content-end gap-2">
              <Button variant="outline-success" onClick={handleExport} disabled={!!exporting}>
                {exporting === 'excel' ? <Spinner animation="border" size="sm" /> : t('billing.downloadExcel')}
              </Button>
              {showAccountsPdfExport && (
                <Button variant="outline-primary" onClick={handleExportPdf} disabled={!!exporting}>
                  {exporting === 'pdf' ? <Spinner animation="border" size="sm" /> : t('billing.downloadPdf')}
                </Button>
              )}
            </Col>
          </Row>

          {showTopUpTransactions && (
            <Card className="mb-4 billing-top-ups-card">
              <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-2">
                <div>
                  <div className="fw-semibold">{t('billing.accountsTopUpsTitle')}</div>
                  <div className="small text-muted">
                    {t('billing.accountsTopUpsHint')}
                    {topUpTotal > topUpRows.length ? ` ${t('billing.accountsTopUpsLimited', { count: topUpRows.length, total: topUpTotal })}` : ''}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline-success"
                  onClick={handleTopUpsExport}
                  disabled={topUpsExporting}
                >
                  {topUpsExporting ? <Spinner animation="border" size="sm" /> : t('billing.downloadExcel')}
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                {topUpsLoading ? (
                  <div className="p-3">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : topUpRows.length === 0 ? (
                  <div className="p-3 text-muted small">{t('billing.accountsNoTopUps')}</div>
                ) : (
                  <div className="table-responsive billing-top-ups-table-scroll">
                    <Table striped bordered hover size="sm" className="mb-0 align-middle">
                      <thead className="billing-accounts-thead-sticky">
                        <tr>
                          <th>{t('billing.date')}</th>
                          <th>{t('billing.accountsStoreColumn')}</th>
                          <th>{t('billing.amount')}</th>
                          <th>{t('billing.accountsTopUpsComment')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topUpRows.map((row) => (
                          <tr key={row.id}>
                            <td className="text-nowrap">{row.date || '—'}</td>
                            <td className="text-body">{row.store_name || row.store_external_id || '—'}</td>
                            <td className="text-success fw-semibold text-nowrap">
                              {row.amount} {t('common.currency.yen')}
                            </td>
                            <td className="text-body text-break">
                              {(row.comment || '').trim() || (row.description || '').trim() || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          <Card className="mb-4 billing-custom-charges-card">
            <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-2">
              <div>
                <div className="fw-semibold">{t('billing.accountsCustomChargesTitle')}</div>
                <div className="small text-muted">
                  {t('billing.accountsCustomChargesHint')}
                  {customChargeTotal > customChargeRows.length
                    ? ` ${t('billing.accountsCustomChargesLimited', { count: customChargeRows.length, total: customChargeTotal })}`
                    : ''}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline-success"
                onClick={handleCustomChargesExport}
                disabled={customChargesExporting}
              >
                {customChargesExporting ? <Spinner animation="border" size="sm" /> : t('billing.downloadExcel')}
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              {customChargesLoading ? (
                <div className="p-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : customChargeRows.length === 0 ? (
                <div className="p-3 text-muted small">{t('billing.accountsNoCustomCharges')}</div>
              ) : (
                <div className="table-responsive billing-custom-charges-table-scroll">
                  <Table striped bordered hover size="sm" className="mb-0 align-middle">
                    <thead className="billing-accounts-thead-sticky">
                      <tr>
                        <th>{t('billing.date')}</th>
                        <th>{t('billing.accountsStoreColumn')}</th>
                        <th>{t('billing.amount')}</th>
                        <th>{t('billing.accountsCustomChargesComment')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customChargeRows.map((row) => (
                        <tr key={row.id}>
                          <td className="text-nowrap">{row.date || '—'}</td>
                          <td className="text-body">{row.store_name || row.store_external_id || '—'}</td>
                          <td className="text-danger fw-semibold text-nowrap">
                            {row.amount} {t('common.currency.yen')}
                          </td>
                          <td className="text-body text-break">
                            {(row.comment || '').trim() || (row.description || '').trim() || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : barcodeRows.length === 0 && !error ? (
            <p className="text-muted">{t('billing.accountsNoBarcodeRows')}</p>
          ) : (
            <>
              <Row className="mb-2 align-items-center flex-wrap g-2">
                <Col xs={12} md="auto">
                  <span className="text-muted small">
                    {t('billing.shownOf', { from, to, total: barcodeTotal })}
                  </span>
                </Col>
                <Col xs={12} md="auto" className="d-flex flex-wrap align-items-center gap-2">
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto', minWidth: '90px' }}
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n} {t('billing.perPage')}</option>
                    ))}
                  </Form.Select>
                  <div className="d-flex align-items-center gap-1">
                    <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                      {t('billing.back')}
                    </Button>
                    <span className="small px-2">{t('billing.pageOf', { page, total: totalPages })}</span>
                    <Button size="sm" variant="outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                      {t('billing.next')}
                    </Button>
                  </div>
                </Col>
              </Row>

              <div className="table-responsive billing-accounts-table-wrap w-100 flex-grow-1 billing-accounts-table-scroll">
                <Table striped bordered hover size="sm" className="mb-0 align-middle billing-accounts-table w-100">
                  <thead className="billing-accounts-thead-sticky">
                    <tr>
                      <th className="billing-accounts-col-thumb d-none d-sm-table-cell" style={{ width: 56 }} />
                      <th className="billing-accounts-col-store">{t('billing.accountsStoreColumn')}</th>
                      <th className="billing-accounts-col-barcode d-none d-md-table-cell">{t('billing.accountsBarcode')}</th>
                      <th className="billing-accounts-col-product">{t('billing.product')}</th>
                      <th className="d-none d-lg-table-cell">{t('billing.accountsNmId')}</th>
                      <th className="d-none d-lg-table-cell">{t('billing.accountsPrice')}</th>
                      <th>{t('billing.accountsTotalCharges')}</th>
                      <th className="d-none d-lg-table-cell">{t('billing.accountsReturnsCount')}</th>
                      <th className="d-none d-lg-table-cell">{t('billing.accountsInSaleOnWarehouse')}</th>
                      <th className="d-none d-lg-table-cell">{t('billing.accountsWhBuyerShipments')}</th>
                      <th className="billing-accounts-col-actions text-nowrap">{t('billing.accountsDetailColumn')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedBarcodeRows.map((row) => {
                      const rk = accountRowKey(row);
                      return (
                      <React.Fragment key={rk}>
                        <tr
                          className={isMobileAccountsView ? 'billing-accounts-mobile-clickable' : undefined}
                          onClick={isMobileAccountsView ? () => toggleExpand(row) : undefined}
                        >
                          <td className="billing-accounts-col-thumb d-none d-sm-table-cell">
                            {row.wb_image_url ? (
                              <img
                                key={`acc-thumb-${rk}-${row.nm_id ?? 'x'}`}
                                src={row.wb_image_url}
                                alt=""
                                loading="lazy"
                                decoding="async"
                                style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }}
                              />
                            ) : accountsThumbsLoading &&
                              row.nm_id != null &&
                              row.seller_id &&
                              row.seller_id !== '__unknown__' ? (
                              <div
                                className="bg-secondary-subtle rounded d-flex align-items-center justify-content-center"
                                style={{ width: 48, height: 48 }}
                              >
                                <Spinner animation="border" size="sm" variant="secondary" />
                              </div>
                            ) : (
                              <div className="bg-secondary-subtle rounded" style={{ width: 48, height: 48 }} />
                            )}
                          </td>
                          <td className="billing-accounts-cell billing-accounts-col-store text-body">{row.store_name || row.seller_id || '—'}</td>
                          <td className="billing-accounts-cell billing-accounts-col-barcode d-none d-md-table-cell"><code className="billing-accounts-mono">{row.barcode === '__no_barcode__' ? '—' : row.barcode}</code></td>
                          <td className="billing-accounts-product billing-accounts-col-product" title={row.product_name}>{row.product_name || '—'}</td>
                          <td className="billing-accounts-cell text-body d-none d-lg-table-cell">{row.nm_id ?? '—'}</td>
                          <td className="billing-accounts-cell text-body d-none d-lg-table-cell">
                            {row.price != null ? String(row.price) : '—'}
                          </td>
                          <td className="text-danger text-nowrap fw-semibold">{row.total_charges_cny}</td>
                          <td className="billing-accounts-cell text-body d-none d-lg-table-cell">{row.returns_count}</td>
                          <td className="billing-accounts-cell text-body d-none d-lg-table-cell">{row.in_sale_no_wh_shipment_count ?? '—'}</td>
                          <td className="billing-accounts-cell text-body d-none d-lg-table-cell">{row.wh_buyer_shipments_count ?? '—'}</td>
                          <td className="billing-accounts-col-actions" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="primary"
                              className="text-nowrap fw-semibold billing-accounts-cta-btn billing-accounts-detail-btn"
                              onClick={() => toggleExpand(row)}
                            >
                              <i className="bi bi-layout-text-sidebar-reverse me-1 d-none d-sm-inline" aria-hidden />
                              <span className="d-sm-none">{expandedAccountKey === rk ? '−' : '+'}</span>
                              <span className="d-none d-sm-inline">{expandedAccountKey === rk ? t('billing.accountsCollapse') : t('billing.accountsExpand')}</span>
                            </Button>
                          </td>
                        </tr>
                        {expandedAccountKey === rk && (
                          <tr>
                            <td colSpan={11} className="billing-accounts-expanded p-3">
                              {isMobileAccountsView && (
                                <div className="small mb-3 p-2 rounded border billing-accounts-mobile-meta">
                                  <div>
                                    <span className="text-muted">{t('billing.accountsBarcode')}: </span>
                                    <code className="billing-accounts-mono">
                                      {row.barcode === '__no_barcode__' ? '—' : row.barcode}
                                    </code>
                                  </div>
                                  <div>
                                    <span className="text-muted">{t('billing.accountsNmId')}: </span>
                                    <span>{row.nm_id ?? '—'}</span>
                                  </div>
                                  <div>
                                    <span className="text-muted">{t('billing.accountsPrice')}: </span>
                                    <span>{row.price != null ? String(row.price) : '—'}</span>
                                  </div>
                                  <div>
                                    <span className="text-muted">{t('billing.accountsReturnsCount')}: </span>
                                    <span>{row.returns_count}</span>
                                  </div>
                                  <div>
                                    <span className="text-muted">{t('billing.accountsInSaleOnWarehouse')}: </span>
                                    <span>{row.in_sale_no_wh_shipment_count ?? '—'}</span>
                                  </div>
                                  <div>
                                    <span className="text-muted">{t('billing.accountsWhBuyerShipments')}: </span>
                                    <span>{row.wh_buyer_shipments_count ?? '—'}</span>
                                  </div>
                                </div>
                              )}
                              {detailLoading === rk && (
                                <Spinner animation="border" size="sm" />
                              )}
                              {detailByAccountKey[rk]?.error && (
                                <Alert variant="warning">{detailByAccountKey[rk].error}</Alert>
                              )}
                              {detailByAccountKey[rk]?.orders && (
                                <div className="d-flex flex-column gap-3">
                                  {detailByAccountKey[rk].orders.map((ord) => {
                                    const lk = accountsChargeLinesKey(rk, ord.order_id);
                                    const linesOpen = !!expandedChargeLines[lk];
                                    const agg = aggregateLogicalExpenses(ord.expenses);
                                    const returnTotal = sumExpensesAmount(ord.expenses);
                                    return (
                                      <Card key={ord.order_id} body className="shadow-sm billing-accounts-return-card">
                                        <div className="fw-bold mb-2 text-body">
                                          {ord.platform_order_no || ord.order_id}{' '}
                                          <Badge bg="info">
                                            {translateOrderStatus(t, ord.current_status, ord.status_display)}
                                          </Badge>
                                        </div>
                                        <div className="small text-muted mb-1">{t('billing.accountsStatusHistory')}</div>
                                        <div className="small text-muted mb-2 fst-italic">
                                          {t('billing.accountsStatusHistoryTranslatedHint')}
                                        </div>
                                        <ul className="small mb-3 ps-3 billing-accounts-status-history">
                                          {ord.status_history.map((h, i) => (
                                            <li key={`${ord.order_id}-${i}`}>
                                              <span className="text-muted">{h.changed_at?.slice(0, 16) || ''}</span>
                                              {' · '}
                                              <span className="text-body">
                                                {translateOrderStatus(t, h.from_status, h.from_display)}
                                                {' → '}
                                                {translateOrderStatus(t, h.to_status, h.to_display)}
                                              </span>
                                            </li>
                                          ))}
                                        </ul>
                                        <div className="small fw-semibold mb-2 text-body">
                                          {t('billing.accountsReturnTotals')}
                                          {': '}
                                          <span className="text-danger">{returnTotal}</span>
                                          {' '}
                                          {t('common.currency.yen')}
                                        </div>
                                        <div className="small text-muted mb-1">{t('billing.accountsChargeType')}</div>
                                        <div className="table-responsive mb-3">
                                          <Table size="sm" bordered className="mb-0 billing-accounts-nested-table">
                                            <thead>
                                              <tr>
                                                <th>{t('billing.accountsChargeType')}</th>
                                                <th>{t('billing.amount')}</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {agg.map((a) => (
                                                <tr key={a.key}>
                                                  <td className="text-body">
                                                    {chargeTypeLabel(t, a.key)}
                                                    <ChargeHint t={t} logicalType={a.key} />
                                                  </td>
                                                  <td className="text-danger">{a.total}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </Table>
                                        </div>
                                        <Button
                                          size="sm"
                                          variant="primary"
                                          className="mb-2 fw-semibold billing-accounts-cta-btn billing-accounts-cta-btn--lines"
                                          onClick={() => setExpandedChargeLines((prev) => ({
                                            ...prev,
                                            [lk]: !prev[lk],
                                          }))}
                                        >
                                          <i className={`bi bi-chevron-${linesOpen ? 'up' : 'down'} me-1`} aria-hidden />
                                          {linesOpen ? t('billing.accountsCollapseChargeLines') : t('billing.accountsExpandChargeLines')}
                                        </Button>
                                        {linesOpen && (
                                          <div className="table-responsive">
                                            <Table size="sm" bordered className="mb-0 billing-accounts-nested-table">
                                              <thead>
                                                <tr>
                                                  <th>{t('billing.accountsChargeType')}</th>
                                                  <th>{t('billing.date')}</th>
                                                  <th>{t('billing.amount')}</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {(ord.expenses || []).map((ex) => {
                                                  const logical = logicalChargeType(ex.type);
                                                  return (
                                                    <tr key={ex.id}>
                                                      <td className="text-body">
                                                        <div>
                                                          {chargeTypeLabel(t, logical)}
                                                          <ChargeHint t={t} logicalType={logical} />
                                                        </div>
                                                        {ex.type === 'balance_top_up' &&
                                                        (ex.client_comment || '').trim() ? (
                                                          <div className="small text-muted mt-1 text-break">
                                                            {(ex.client_comment || '').trim()}
                                                          </div>
                                                        ) : null}
                                                      </td>
                                                      <td className="text-body">{ex.billing_date || '—'}</td>
                                                      <td className="text-danger">{ex.amount}</td>
                                                    </tr>
                                                  );
                                                })}
                                              </tbody>
                                            </Table>
                                          </div>
                                        )}
                                      </Card>
                                    );
                                  })}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
    </div>
  );
};

export default BillingsPage;
